import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Grid, TextField, Button, MenuItem,
    CircularProgress, Divider, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Checkbox, Chip, Alert, Dialog, DialogTitle,
    DialogContent, DialogActions, FormControlLabel, Switch, Card, CardContent,
    Tabs, Tab
} from '@mui/material';
import { Autorenew, CheckCircle, Cancel, PersonOff, Warning } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../services/api';

const AutoRenewal = () => {
    const [activeTab, setActiveTab] = useState(0); // 0 = Class Wise, 1 = 1-Click Bulk
    const [classes, setClasses] = useState([]);
    const [academicYears, setAcademicYears] = useState([]);
    const [sections, setSections] = useState([]);
    const [targetSections, setTargetSections] = useState([]);
    const [loading, setLoading] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewData, setPreviewData] = useState(null);

    // Single class promotion state
    const [formData, setFormData] = useState({
        sourceAcademicYearId: '',
        sourceClassId: '',
        sourceSectionId: '',
        targetAcademicYearId: '',
        targetClassId: '',
        targetSectionId: '',
        resetRollNumbers: false,
        markHighestClassAsGraduated: false,
        isHighestClass: false
    });

    const [confirmDialog, setConfirmDialog] = useState(false);
    const [result, setResult] = useState(null);

    // Bulk promotion state
    const [bulkFormData, setBulkFormData] = useState({
        sourceAcademicYearId: '',
        targetAcademicYearId: '',
        highestClassId: '',
        resetRollNumbers: false
    });
    const [bulkResult, setBulkResult] = useState(null);
    const [bulkConfirmDialog, setBulkConfirmDialog] = useState(false);

    useEffect(() => {
        fetchClasses();
        fetchAcademicYears();
    }, []);

    const fetchClasses = async () => {
        try {
            const res = await api.get('/classes');
            const classList = res.data.data || [];
            setClasses(classList);
            
            // Set default highest class for bulk promotion
            if (classList.length > 0) {
                // Find Class 10 or 10th
                const class10 = classList.find(c => c.name.toLowerCase() === 'class 10' || c.name.toLowerCase() === '10th');
                const defaultHighest = class10 || classList.reduce((max, c) => c.numericValue > max.numericValue ? c : max, classList[0]);
                if (defaultHighest) {
                    setBulkFormData(prev => ({ ...prev, highestClassId: defaultHighest.id }));
                }
            }
        } catch (error) {
            console.error("Failed to fetch classes");
        }
    };

    const fetchAcademicYears = async () => {
        try {
            const res = await api.get('/academic-years');
            const years = res.data.data || [];
            setAcademicYears(years);
            
            // Set current year as source by default
            const current = years.find(y => y.isCurrent);
            if (current) {
                const parseStartYear = (name) => {
                    if (!name) return 0;
                    const parts = name.split('-');
                    return parseInt(parts[0]) || 0;
                };

                const currentStartYear = parseStartYear(current.name);
                const nextYear = years.find(y => {
                    const startYear = parseStartYear(y.name);
                    return startYear === currentStartYear + 1;
                });

                setFormData(prev => ({
                    ...prev,
                    sourceAcademicYearId: current.id,
                    targetAcademicYearId: nextYear?.id || ''
                }));

                setBulkFormData(prev => ({
                    ...prev,
                    sourceAcademicYearId: current.id,
                    targetAcademicYearId: nextYear?.id || ''
                }));
            }
        } catch (error) {
            console.error("Failed to fetch academic years");
        }
    };

    const handleSourceClassChange = async (e) => {
        const classId = e.target.value;
        setFormData({ ...formData, sourceClassId: classId, sourceSectionId: '' });
        setPreviewData(null);
        try {
            const res = await api.get(`/classes/${classId}/sections`);
            setSections(res.data.data || []);

            // Auto-select next class as target
            const selectedClass = classes.find(c => c.id === classId);
            if (selectedClass) {
                const nextClass = classes.find(c => c.numericValue === selectedClass.numericValue + 1);
                if (nextClass) {
                    setFormData(prev => ({ ...prev, sourceClassId: classId, targetClassId: nextClass.id, isHighestClass: false }));
                    const targetRes = await api.get(`/classes/${nextClass.id}/sections`);
                    setTargetSections(targetRes.data.data || []);
                } else {
                    setFormData(prev => ({ ...prev, sourceClassId: classId, targetClassId: '', isHighestClass: true }));
                    setTargetSections([]);
                }
            }
        } catch (error) {
            console.error("Failed to fetch sections");
        }
    };

    const handleTargetClassChange = async (e) => {
        const classId = e.target.value;
        setFormData({ ...formData, targetClassId: classId, targetSectionId: '' });
        try {
            const res = await api.get(`/classes/${classId}/sections`);
            setTargetSections(res.data.data || []);
        } catch (error) {
            console.error("Failed to fetch target sections");
        }
    };

    const handlePreview = async () => {
        if (!formData.sourceClassId || !formData.sourceAcademicYearId) {
            toast.warning("Please select source class and academic year");
            return;
        }

        setPreviewLoading(true);
        try {
            const res = await api.post('/students/promotion-preview', {
                sourceClassId: formData.sourceClassId,
                sourceSectionId: formData.sourceSectionId || undefined,
                sourceAcademicYearId: formData.sourceAcademicYearId
            });
            setPreviewData(res.data.data);
        } catch (error) {
            toast.error("Failed to load preview");
        } finally {
            setPreviewLoading(false);
        }
    };

    const handlePromote = async () => {
        setLoading(true);
        try {
            const res = await api.post('/students/auto-renew', formData);
            setResult(res.data.data);
            toast.success(res.data.message);
            setPreviewData(null);
            setConfirmDialog(false);
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to promote students");
        } finally {
            setLoading(false);
        }
    };

    const handleBulkPromote = async () => {
        setLoading(true);
        try {
            const res = await api.post('/students/bulk-promote-all', bulkFormData);
            setBulkResult(res.data.data);
            toast.success(res.data.message);
            setBulkConfirmDialog(false);
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to perform bulk promotion");
        } finally {
            setLoading(false);
        }
    };

    const getSourceClassName = () => {
        const cls = classes.find(c => c.id === formData.sourceClassId);
        return cls?.name || '';
    };

    const getTargetClassName = () => {
        const cls = classes.find(c => c.id === formData.targetClassId);
        return cls?.name || 'Graduated';
    };

    const getAcademicYearName = (id) => {
        const year = academicYears.find(y => y.id === id);
        return year?.name || '';
    };

    const getBulkPreviewFlows = () => {
        if (!bulkFormData.sourceAcademicYearId) return [];
        let sourceCls = classes.filter(c => c.academicYearId === bulkFormData.sourceAcademicYearId);
        if (sourceCls.length === 0) sourceCls = classes;

        const sorted = [...sourceCls].sort((a, b) => a.numericValue - b.numericValue);
        const highest = classes.find(c => c.id === bulkFormData.highestClassId);

        return sorted.map(cls => {
            const isGrad = highest && cls.numericValue >= highest.numericValue;
            let targetName = 'Graduated';
            if (!isGrad) {
                const nextCls = sorted.find(c => c.numericValue === cls.numericValue + 1);
                targetName = nextCls ? nextCls.name : `Class ${cls.numericValue + 1}`;
            }
            return {
                sourceClass: cls.name,
                targetClass: targetName,
                isGraduating: isGrad
            };
        });
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Autorenew color="primary" />
                Student Promotion & Auto-Renewal
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
                Promote students to their next classes for the new academic year.
                Students with Leaving Certificate (LC) or Transfer Certificate (TC) will be automatically excluded.
            </Typography>

            <Paper sx={{ mb: 3, mt: 2, borderRadius: 2 }}>
                <Tabs
                    value={activeTab}
                    onChange={(e, newValue) => {
                        setActiveTab(newValue);
                        setResult(null);
                        setBulkResult(null);
                    }}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="fullWidth"
                >
                    <Tab label="Class Wise Promotion" />
                    <Tab label="1-Click Bulk Promotion (LKG to 10th)" />
                </Tabs>
            </Paper>

            {/* TAB 0: Class Wise Promotion */}
            {activeTab === 0 && (
                <>
                    {/* Selection Form */}
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>Select Source & Target</Typography>

                        <Grid container spacing={3}>
                            {/* Source Selection */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                    From (Current Year)
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Academic Year"
                                    value={formData.sourceAcademicYearId}
                                    onChange={e => setFormData({ ...formData, sourceAcademicYearId: e.target.value })}
                                >
                                    {academicYears.map(y => (
                                        <MenuItem key={y.id} value={y.id}>
                                            {y.name} {y.isCurrent && '(Current)'}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Class"
                                    value={formData.sourceClassId}
                                    onChange={handleSourceClassChange}
                                >
                                    {classes.map(cls => (
                                        <MenuItem key={cls.id} value={cls.id}>{cls.name}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Section (Optional)"
                                    value={formData.sourceSectionId}
                                    onChange={e => setFormData({ ...formData, sourceSectionId: e.target.value })}
                                    disabled={!formData.sourceClassId}
                                >
                                    <MenuItem value="">All Sections</MenuItem>
                                    {sections.map(sec => (
                                        <MenuItem key={sec.id} value={sec.id}>{sec.name}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            {/* Target Selection */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="secondary" gutterBottom>
                                    To (Next Year)
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Target Academic Year"
                                    value={formData.targetAcademicYearId}
                                    onChange={e => setFormData({ ...formData, targetAcademicYearId: e.target.value })}
                                >
                                    {academicYears.map(y => (
                                        <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Target Class"
                                    value={formData.targetClassId}
                                    onChange={handleTargetClassChange}
                                    disabled={formData.isHighestClass}
                                >
                                    {classes.map(cls => (
                                        <MenuItem key={cls.id} value={cls.id}>{cls.name}</MenuItem>
                                    ))}
                                </TextField>
                                {formData.isHighestClass && (
                                    <Typography variant="caption" color="warning.main">
                                        This is the highest class - students will be marked as Graduated
                                    </Typography>
                                )}
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Target Section (Optional)"
                                    value={formData.targetSectionId}
                                    onChange={e => setFormData({ ...formData, targetSectionId: e.target.value })}
                                    disabled={!formData.targetClassId || formData.isHighestClass}
                                >
                                    <MenuItem value="">Keep Same Section</MenuItem>
                                    {targetSections.map(sec => (
                                        <MenuItem key={sec.id} value={sec.id}>{sec.name}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            {/* Options */}
                            <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="subtitle2" gutterBottom>Options</Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.resetRollNumbers}
                                            onChange={e => setFormData({ ...formData, resetRollNumbers: e.target.checked })}
                                        />
                                    }
                                    label="Reset Roll Numbers (1, 2, 3...)"
                                />
                            </Grid>
                            {formData.isHighestClass && (
                                <Grid item xs={12} md={4}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.markHighestClassAsGraduated}
                                                onChange={e => setFormData({ ...formData, markHighestClassAsGraduated: e.target.checked })}
                                            />
                                        }
                                        label="Mark as Graduated"
                                    />
                                </Grid>
                            )}

                            {/* Action Buttons */}
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                    <Button
                                        variant="outlined"
                                        onClick={handlePreview}
                                        disabled={previewLoading || !formData.sourceClassId}
                                        startIcon={previewLoading ? <CircularProgress size={20} /> : null}
                                    >
                                        Preview Students
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => setConfirmDialog(true)}
                                        disabled={!previewData || previewData.eligible.count === 0 || !formData.targetAcademicYearId}
                                        startIcon={<Autorenew />}
                                    >
                                        Promote Students
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Preview Section */}
                    {previewData && (
                        <Grid container spacing={3}>
                            {/* Summary Cards */}
                            <Grid item xs={12} md={4}>
                                <Card sx={{ bgcolor: 'success.light' }}>
                                    <CardContent>
                                        <Typography variant="h4" color="success.dark">{previewData.eligible.count}</Typography>
                                        <Typography variant="body2">Eligible for Promotion</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Card sx={{ bgcolor: 'warning.light' }}>
                                    <CardContent>
                                        <Typography variant="h4" color="warning.dark">{previewData.ineligible.count}</Typography>
                                        <Typography variant="body2">Will be Skipped (LC/TC)</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Card sx={{ bgcolor: 'grey.200' }}>
                                    <CardContent>
                                        <Typography variant="h4">{previewData.total}</Typography>
                                        <Typography variant="body2">Total Students</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Eligible Students Table */}
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 2 }}>
                                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CheckCircle color="success" /> Eligible Students ({previewData.eligible.count})
                                    </Typography>
                                    <TableContainer sx={{ maxHeight: 400 }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Roll No</TableCell>
                                                    <TableCell>Name</TableCell>
                                                    <TableCell>Section</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {previewData.eligible.students.map(std => (
                                                    <TableRow key={std.id}>
                                                        <TableCell>{std.rollNumber}</TableCell>
                                                        <TableCell>{std.user?.firstName} {std.user?.lastName}</TableCell>
                                                        <TableCell>{std.section?.name}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                            </Grid>

                            {/* Ineligible Students Table */}
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 2 }}>
                                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <PersonOff color="warning" /> Will be Skipped ({previewData.ineligible.count})
                                    </Typography>
                                    {previewData.ineligible.count === 0 ? (
                                        <Alert severity="success">No students with LC/TC certificates</Alert>
                                    ) : (
                                        <TableContainer sx={{ maxHeight: 400 }}>
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Roll No</TableCell>
                                                        <TableCell>Name</TableCell>
                                                        <TableCell>Reason</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {previewData.ineligible.students.map(std => (
                                                        <TableRow key={std.id}>
                                                            <TableCell>{std.rollNumber}</TableCell>
                                                            <TableCell>{std.user?.firstName} {std.user?.lastName}</TableCell>
                                                            <TableCell>
                                                                <Chip label={std.reason} size="small" color="warning" />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </Paper>
                            </Grid>
                        </Grid>
                    )}

                    {/* Result Section */}
                    {result && (
                        <Paper sx={{ p: 3, mt: 3, bgcolor: 'success.light' }}>
                            <Typography variant="h6" gutterBottom>Promotion Complete!</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={4}>
                                    <Typography variant="h4" color="success.dark">{result.promoted}</Typography>
                                    <Typography>Students Promoted</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="h4" color="info.dark">{result.graduated}</Typography>
                                    <Typography>Students Graduated</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="h4" color="warning.dark">{result.skipped}</Typography>
                                    <Typography>Students Skipped</Typography>
                                </Grid>
                            </Grid>
                            <Typography variant="body2" sx={{ mt: 2 }}>
                                Target: {result.targetClass || 'Graduated'} - {result.targetAcademicYear}
                            </Typography>
                        </Paper>
                    )}
                </>
            )}

            {/* TAB 1: 1-Click Bulk Promotion */}
            {activeTab === 1 && (
                <>
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>1-Click Bulk Promotion Configuration</Typography>
                        <Alert severity="warning" sx={{ mb: 3 }}>
                            This tool will promote all students in all classes (LKG to 10th/12th) from the source academic year to the target academic year in a single transaction. Target classes and divisions/sections will be automatically created if they do not exist.
                        </Alert>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Source Academic Year (From)"
                                    value={bulkFormData.sourceAcademicYearId}
                                    onChange={e => setBulkFormData({ ...bulkFormData, sourceAcademicYearId: e.target.value })}
                                >
                                    {academicYears.map(y => (
                                        <MenuItem key={y.id} value={y.id}>
                                            {y.name} {y.isCurrent && '(Current)'}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Target Academic Year (To)"
                                    value={bulkFormData.targetAcademicYearId}
                                    onChange={e => setBulkFormData({ ...bulkFormData, targetAcademicYearId: e.target.value })}
                                >
                                    {academicYears.map(y => (
                                        <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Highest Class (Graduating Class)"
                                    value={bulkFormData.highestClassId}
                                    onChange={e => setBulkFormData({ ...bulkFormData, highestClassId: e.target.value })}
                                    helperText="Students in this class and above will be marked as Graduated"
                                >
                                    {classes.map(cls => (
                                        <MenuItem key={cls.id} value={cls.id}>{cls.name}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={bulkFormData.resetRollNumbers}
                                            onChange={e => setBulkFormData({ ...bulkFormData, resetRollNumbers: e.target.checked })}
                                        />
                                    }
                                    label="Reset student roll numbers in target classes"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Button
                                    variant="contained"
                                    color="error"
                                    size="large"
                                    onClick={() => setBulkConfirmDialog(true)}
                                    disabled={!bulkFormData.sourceAcademicYearId || !bulkFormData.targetAcademicYearId || !bulkFormData.highestClassId || loading}
                                    startIcon={<Autorenew />}
                                    sx={{ mt: 1 }}
                                >
                                    {loading ? 'Processing...' : '1-Click Bulk Promote All Classes'}
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Promotion flow preview */}
                    {bulkFormData.sourceAcademicYearId && bulkFormData.highestClassId && (
                        <Paper sx={{ p: 3, mb: 3 }}>
                            <Typography variant="h6" gutterBottom>Promotion Flows Preview</Typography>
                            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 350 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                            <TableCell><strong>Source Class</strong></TableCell>
                                            <TableCell><strong>Arrow</strong></TableCell>
                                            <TableCell><strong>Target Class / Destination</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {getBulkPreviewFlows().map((flow, index) => (
                                            <TableRow key={index} hover>
                                                <TableCell>{flow.sourceClass}</TableCell>
                                                <TableCell style={{ color: flow.isGraduating ? '#d32f2f' : '#2e7d32' }}>➔</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={flow.targetClass}
                                                        size="small"
                                                        color={flow.isGraduating ? 'error' : 'success'}
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    )}

                    {/* Bulk Result Section */}
                    {bulkResult && (
                        <Paper sx={{ p: 3, mt: 3, bgcolor: '#e8f5e9', border: '1px solid #c8e6c9' }}>
                            <Typography variant="h6" gutterBottom color="success.dark" fontWeight="bold">
                                Bulk Promotion Complete!
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                Successfully processed promotion from academic year <strong>{getAcademicYearName(bulkFormData.sourceAcademicYearId)}</strong> to <strong>{getAcademicYearName(bulkFormData.targetAcademicYearId)}</strong>.
                            </Typography>
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={12} md={4}>
                                    <Card sx={{ bgcolor: '#c8e6c9' }}>
                                        <CardContent>
                                            <Typography variant="h4" color="success.dark" fontWeight="bold">
                                                {bulkResult.promoted}
                                            </Typography>
                                            <Typography variant="body2">Students Promoted</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Card sx={{ bgcolor: '#bbdefb' }}>
                                        <CardContent>
                                            <Typography variant="h4" color="blue" fontWeight="bold">
                                                {bulkResult.graduated}
                                            </Typography>
                                            <Typography variant="body2">Students Graduated</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Card sx={{ bgcolor: '#ffe0b2' }}>
                                        <CardContent>
                                            <Typography variant="h4" color="orange" fontWeight="bold">
                                                {bulkResult.skipped}
                                            </Typography>
                                            <Typography variant="body2">Students Skipped (LC/TC)</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>

                            <Typography variant="subtitle2" gutterBottom>Class-wise Promotion Details</Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                            <TableCell><strong>Source Class</strong></TableCell>
                                            <TableCell><strong>Target Class</strong></TableCell>
                                            <TableCell align="right"><strong>Promoted</strong></TableCell>
                                            <TableCell align="right"><strong>Graduated</strong></TableCell>
                                            <TableCell align="right"><strong>Skipped (LC/TC)</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {bulkResult.summary?.map((row, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell>{row.sourceClass}</TableCell>
                                                <TableCell>{row.targetClass}</TableCell>
                                                <TableCell align="right" style={{ color: row.promoted > 0 ? '#2e7d32' : 'inherit' }}>
                                                    {row.promoted}
                                                </TableCell>
                                                <TableCell align="right" style={{ color: row.graduated > 0 ? '#1565c0' : 'inherit' }}>
                                                    {row.graduated}
                                                </TableCell>
                                                <TableCell align="right" style={{ color: row.skipped > 0 ? '#e65100' : 'inherit' }}>
                                                    {row.skipped}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    )}
                </>
            )}

            {/* Confirmation Dialog (Single Class) */}
            <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Warning color="warning" /> Confirm Promotion
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This action will promote students and cannot be easily undone.
                    </Alert>
                    <Typography gutterBottom>
                        <strong>{previewData?.eligible?.count || 0}</strong> students will be promoted from{' '}
                        <strong>{getSourceClassName()}</strong> ({getAcademicYearName(formData.sourceAcademicYearId)}) to{' '}
                        <strong>{getTargetClassName()}</strong> ({getAcademicYearName(formData.targetAcademicYearId)})
                    </Typography>
                    {previewData?.ineligible?.count > 0 && (
                        <Typography color="warning.main" gutterBottom>
                            {previewData.ineligible.count} students with LC/TC will be skipped.
                        </Typography>
                    )}
                    {formData.resetRollNumbers && (
                        <Typography variant="body2" color="text.secondary">
                            ✓ Roll numbers will be reset (1, 2, 3...)
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handlePromote}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <Autorenew />}
                    >
                        {loading ? 'Processing...' : 'Confirm Promotion'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation Dialog (Bulk Promotion) */}
            <Dialog open={bulkConfirmDialog} onClose={() => setBulkConfirmDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#d32f2f' }}>
                    <Warning color="error" /> CRITICAL: Confirm 1-Click Bulk Student Promotion
                </DialogTitle>
                <DialogContent>
                    <Alert severity="error" sx={{ mb: 2, fontWeight: 'bold' }}>
                        WARNING: This will promote ALL students in ALL classes in the selected academic year at once! This is a massive operation.
                    </Alert>
                    <Typography gutterBottom>
                        Are you sure you want to perform bulk student promotion from{' '}
                        <strong>{getAcademicYearName(bulkFormData.sourceAcademicYearId)}</strong> to{' '}
                        <strong>{getAcademicYearName(bulkFormData.targetAcademicYearId)}</strong>?
                    </Typography>
                    <Typography gutterBottom variant="body2" color="text.secondary">
                        - Students in the graduating class and above will be marked as Graduated.<br />
                        - Target classes and sections will be created dynamically if they don't exist.<br />
                        - Students with LC/TC will be automatically skipped.<br />
                        {bulkFormData.resetRollNumbers && <span>- Roll numbers will be reset (1, 2, 3...).</span>}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBulkConfirmDialog(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleBulkPromote}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <Autorenew />}
                    >
                        {loading ? 'Processing...' : 'Confirm Bulk Promote All'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AutoRenewal;
